import { ACCESS_TOKEN } from '../assets/constants'
import { Security, Segment } from '../proto/generated/security'
import { currentAdapter } from '../services/apis/axiosClient'
import { ensureValidAccessToken } from '../services/auth/tokenRefresh'
import { waitForInitialLoadSlot } from './initialLoadGate'
import { getSecurityKey } from './security'
import { createSecuritiesSyncSession, SecuritiesSyncSession } from './securitiesStore/client'
import { buildOptionSecurity, parseOptionKey } from './securitiesStore/optionSynth'
import type { OptionSeriesDef, RevalidateOutcome, SecuritiesSnapshot, SecuritiesSyncSource, StoredSecurities, SyncTimings } from './securitiesStore/types'

const isCmSpotListing = (security: Security) =>
    (security.segment === undefined || security.segment === Segment.CM) && !security.derived

const pushIndexed = (index: Map<string, Security[]>, key: string, security: Security) => {
    const bucket = index.get(key)
    if (bucket) bucket.push(security)
    else index.set(key, [security])
}

const elapsed = (since: number) => `${Math.round(performance.now() - since)}ms`
const describe = (timings: SyncTimings) =>
    Object.entries(timings)
        .map(([phase, ms]) => `${phase} ${Math.round(ms)}ms`)
        .join(', ')

/**
 * In-memory view of the securities master.
 *
 * Memory holds the hot set only: every cash (CM) listing, derived and futures instrument,
 * the compact option-series definitions, and any security that has been requested. Option
 * securities are synthesised from their series definition the first time they are looked up
 * and memoised.
 *
 * Loading is stale-while-revalidate: the snapshot persisted in IndexedDB is applied as soon
 * as it is read (no network involved, so it does not wait for the initial-load gate), and
 * `loadSecurities` resolves at that point. A conditional request then revalidates it in the
 * background once the gate allows, and only a 200 (new ETag) replaces the data.
 */
class SecurityManager {
    /** Hot set keyed by security key. The Map instance is stable for the manager's lifetime. */
    readonly securities: Map<string, Security> = new Map()

    private cashSecurities: Security[] = []
    private cashByIsin: Map<string, Security[]> = new Map()
    private cashByCompanyCode: Map<string, Security[]> = new Map()
    private optionSeries: Map<string, OptionSeriesDef> = new Map()
    private loading: Promise<void> | null = null
    private appliedValidator: string | null = null
    lastLoadSource: SecuritiesSyncSource | null = null
    /** Diagnostics for the last snapshot applied and the last revalidation (also printed to the console). */
    lastLoad: { source: SecuritiesSyncSource; totalMs: number; timings: SyncTimings; at: number } | null = null
    lastRevalidation: { result: RevalidateOutcome['result']; totalMs: number; timings: SyncTimings; at: number } | null = null

    loadSecurities(): Promise<void> {
        if (this.loading) return this.loading
        // The promise is kept after success so every later caller (App on mount, on token
        // refresh, embedded pages) joins the one load that index.tsx starts before React mounts;
        // only a failed load is retried.
        this.loading = this.runLoad().catch((error) => {
            this.loading = null
            throw error
        })
        return this.loading
    }

    private async runLoad(): Promise<void> {
        const startedAt = performance.now()
        let session = await createSecuritiesSyncSession()
        let stored: StoredSecurities
        try {
            stored = await session.loadStored()
        } catch (error) {
            // Worker failed to boot or crashed before answering: redo the cycle on the main thread.
            console.warn('[securities] worker unavailable, loading inline', error)
            session.close()
            session = await createSecuritiesSyncSession({ inline: true })
            stored = await session.loadStored()
        }

        if (stored.snapshot) {
            this.apply(stored.snapshot, stored.validator, 'idb', startedAt, stored.timings)
        }

        const revalidation = this.revalidate(session).finally(() => session.close())
        if (stored.snapshot) {
            // Already usable; a newer master (if any) is swapped in when the 200 arrives.
            void revalidation.catch((error) => console.warn('[securities] revalidation failed, using stored copy', error))
            return
        }
        const outcome = await revalidation
        if (outcome.result === 'failed') throw new Error(outcome.message)
    }

    private async revalidate(session: SecuritiesSyncSession): Promise<RevalidateOutcome> {
        // Same ordering rules as the axios client: never ahead of the profile/candle requests.
        await waitForInitialLoadSlot('/v2/security/all')
        const token = (await ensureValidAccessToken().catch(() => null)) ?? localStorage.getItem(ACCESS_TOKEN)
        const startedAt = performance.now()
        // Same backend the axios client talks to (differs per build: tick2trade vs shoonya).
        const outcome = await session.revalidate({ baseUrl: currentAdapter.getBaseUrl(), token }, (snapshot, validator) =>
            this.apply(snapshot, validator, 'network', startedAt),
        )
        this.lastRevalidation = { result: outcome.result, totalMs: Math.round(performance.now() - startedAt), timings: outcome.timings, at: Date.now() }
        console.info(`[securities] revalidated: ${outcome.result} in ${elapsed(startedAt)} (${describe(outcome.timings)})`)
        return outcome
    }

    private apply(
        snapshot: SecuritiesSnapshot,
        validator: string | null,
        source: SecuritiesSyncSource,
        startedAt: number,
        timings: SyncTimings = {},
    ): void {
        if (validator && validator === this.appliedValidator && this.securities.size > 0) {
            console.info(`[securities] ${source} snapshot unchanged (${validator}), kept current set`)
            return
        }
        const applyStartedAt = performance.now()
        this.applySnapshot(snapshot)
        timings.apply = performance.now() - applyStartedAt
        this.appliedValidator = validator
        this.lastLoadSource = source
        this.lastLoad = { source, totalMs: Math.round(performance.now() - startedAt), timings, at: Date.now() }
        console.info(
            `[securities] loaded from ${source} in ${elapsed(startedAt)}: ` +
                `${snapshot.securities.length} securities, ${snapshot.optionSeries.length} option series (${describe(timings)})`,
        )
    }

    private applySnapshot(snapshot: SecuritiesSnapshot): void {
        this.securities.clear()
        this.cashSecurities = []
        this.cashByIsin = new Map()
        this.cashByCompanyCode = new Map()
        this.optionSeries = new Map()
        snapshot.securities.forEach((sec) => this.insert(sec))
        snapshot.optionSeries.forEach((def) => this.optionSeries.set(def.id, def))
    }

    private insert(security: Security): void {
        this.securities.set(getSecurityKey(security.exchange, security.code), security)
        if (!isCmSpotListing(security)) return
        this.cashSecurities.push(security)
        const isin = security.isin?.trim().toUpperCase()
        if (isin) pushIndexed(this.cashByIsin, isin, security)
        if (security.companyCode) pushIndexed(this.cashByCompanyCode, security.companyCode, security)
    }

    private synthesizeOption(key: string): Security | undefined {
        const parsed = parseOptionKey(key)
        if (!parsed) return undefined
        const def = this.optionSeries.get(parsed.seriesId)
        if (!def || !def.strikes.includes(parsed.strike)) return undefined
        const underlying = this.securities.get(getSecurityKey(def.exchange, def.underlyingCode))
        if (!underlying) return undefined
        const security = buildOptionSecurity(def, underlying, parsed.strike, parsed.optionType)
        this.securities.set(key, security)
        return security
    }

    setSecurity(security: Security): void {
        const key = getSecurityKey(security.exchange, security.code)
        if (!this.securities.has(key)) this.insert(security)
    }

    getSecurities(keys: string[]): Security[] {
        return keys.map((key) => this.getSecurity(key)).filter((sec): sec is Security => sec !== undefined)
    }

    /** Cash (CM spot) listings only. Derivatives are never enumerated from the manager. */
    getAllSecurities(): Security[] {
        return this.cashSecurities
    }

    getCmListingsByIsin(isin: string): Security[] {
        return this.cashByIsin.get(isin.trim().toUpperCase()) ?? []
    }

    getCmListingsByCompanyCode(companyCode: string): Security[] {
        return this.cashByCompanyCode.get(companyCode) ?? []
    }

    getOptionSeries(seriesId: string): OptionSeriesDef | undefined {
        return this.optionSeries.get(seriesId)
    }

    addSecurities(securities: Security[]) {
        securities.forEach((sec) => this.setSecurity(sec))
    }

    getSecurity(key: string): Security | undefined {
        if (!key) return undefined
        return this.securities.get(key) ?? this.synthesizeOption(key)
    }
}

export default SecurityManager
